/**
 * Main App Component
 */
import React, { useEffect } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
} from "react-router-dom";
import useAuthStore from "./store/authStore";
import LandingPage from "./pages/LandingPage";
import AboutUs from "./pages/AboutUs";
import Login from "./pages/Login";
import Register from "./pages/Register";
import OAuthCallback from "./pages/OAuthCallback";
import NotFound from "./pages/NotFound";
import Dashboard from "./pages/Dashboard";
import WorkflowEditorPage from "./pages/WorkflowEditorPage";
import Pricing from "./pages/Pricing";
import Templates from "./pages/Templates";
import MyAgents from "./pages/MyAgents";
import Settings from "./pages/Settings";
import Integrations from "./pages/Integrations";
import HRAutomationTemplate from "./pages/HRAutomationTemplate";
import CalendarSettings from "./pages/CalendarSettings";
import BookingPages from "./pages/BookingPages";
import PublicBookingPage from "./pages/PublicBookingPage";
import AIBookingPage from "./pages/AIBookingPage";
import SelectLinkType from "./pages/SelectLinkType";
import CreateOneOnOneInvite from "./pages/CreateOneOnOneInvite";
import CreateGroupInvite from "./pages/CreateGroupInvite";
import CreateMeetingPoll from "./pages/CreateMeetingPoll";
import SchedulerDashboard from "./pages/SchedulerDashboard";
import SchedulerSettings from "./pages/SchedulerSettings";
import Events from "./pages/Events";
import CreateEvent from "./pages/CreateEvent";
import TermsAndConditions from "./pages/TermsAndConditions";
import PrivacyPolicy from "./pages/PrivacyPolicy";
import Navbar from "./components/Navbar";
import ToastContainer from "./components/Toast";
import "./App.css";

// Protected Route wrapper
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated } = useAuthStore();
  return isAuthenticated ? children : <Navigate to="/login" />;
};

// Layout wrapper for authenticated pages
const AuthenticatedLayout = ({ children }) => {
  const { user, logout } = useAuthStore();
  return (
    <div>
      <Navbar isAuthenticated={true} user={user} onLogout={logout} />
      {children}
    </div>
  );
};

function App() {
  const { fetchUser, isAuthenticated } = useAuthStore();

  useEffect(() => {
    if (isAuthenticated) {
      fetchUser();
    }
  }, [isAuthenticated, fetchUser]);

  return (
    <Router>
      <div className="App">
        <ToastContainer />
        <Routes>
          {/* Public routes */}
          <Route path="/" element={<LandingPage />} />
          <Route path="/about" element={<AboutUs />} />
          <Route path="/pricing" element={<Pricing />} />
          <Route path="/templates" element={<Templates />} />
          <Route path="/templates/hr-automation" element={<HRAutomationTemplate />} />
          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<Register />} />
          <Route path="/auth/callback" element={<OAuthCallback />} />

          {/* Legal pages - public */}
          <Route path="/terms" element={<TermsAndConditions />} />
          <Route path="/privacy" element={<PrivacyPolicy />} />

          {/* Public booking pages - no auth required */}
          <Route path="/book/:slug" element={<PublicBookingPage />} />
          <Route path="/ai-book/:slug" element={<AIBookingPage />} />

          {/* Protected routes */}
          <Route
            path="/dashboard"
            element={
              <ProtectedRoute>
                <AuthenticatedLayout>
                  <Dashboard />
                </AuthenticatedLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/my-agents"
            element={
              <ProtectedRoute>
                <AuthenticatedLayout>
                  <MyAgents />
                </AuthenticatedLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/integrations"
            element={
              <ProtectedRoute>
                <AuthenticatedLayout>
                  <Integrations />
                </AuthenticatedLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/settings"
            element={
              <ProtectedRoute>
                <AuthenticatedLayout>
                  <Settings />
                </AuthenticatedLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/workflows/:workflowId"
            element={
              <ProtectedRoute>
                <AuthenticatedLayout>
                  <WorkflowEditorPage />
                </AuthenticatedLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/calendar/settings"
            element={
              <ProtectedRoute>
                <AuthenticatedLayout>
                  <CalendarSettings />
                </AuthenticatedLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/calendar/booking-pages"
            element={
              <ProtectedRoute>
                <AuthenticatedLayout>
                  <BookingPages />
                </AuthenticatedLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/calendar/select-link-type"
            element={
              <ProtectedRoute>
                <AuthenticatedLayout>
                  <SelectLinkType />
                </AuthenticatedLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/calendar/create-invite/one-on-one"
            element={
              <ProtectedRoute>
                <AuthenticatedLayout>
                  <CreateOneOnOneInvite />
                </AuthenticatedLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/calendar/create-invite/group"
            element={
              <ProtectedRoute>
                <AuthenticatedLayout>
                  <CreateGroupInvite />
                </AuthenticatedLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/calendar/create-invite/poll"
            element={
              <ProtectedRoute>
                <AuthenticatedLayout>
                  <CreateMeetingPoll />
                </AuthenticatedLayout>
              </ProtectedRoute>
            }
          />

          {/* Scheduler routes - AgentGarden-style interface */}
          <Route
            path="/scheduler"
            element={
              <ProtectedRoute>
                <AuthenticatedLayout>
                  <SchedulerDashboard />
                </AuthenticatedLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/scheduler/settings"
            element={
              <ProtectedRoute>
                <AuthenticatedLayout>
                  <SchedulerSettings />
                </AuthenticatedLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/scheduler/availability"
            element={
              <ProtectedRoute>
                <AuthenticatedLayout>
                  <CalendarSettings />
                </AuthenticatedLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/scheduler/events"
            element={
              <ProtectedRoute>
                <AuthenticatedLayout>
                  <BookingPages />
                </AuthenticatedLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/scheduler/links"
            element={
              <ProtectedRoute>
                <AuthenticatedLayout>
                  <BookingPages />
                </AuthenticatedLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/scheduler/links/new"
            element={
              <ProtectedRoute>
                <AuthenticatedLayout>
                  <SelectLinkType />
                </AuthenticatedLayout>
              </ProtectedRoute>
            }
          />

          {/* Events routes - Luma-style event pages */}
          <Route
            path="/events"
            element={
              <ProtectedRoute>
                <AuthenticatedLayout>
                  <Events />
                </AuthenticatedLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/events/new"
            element={
              <ProtectedRoute>
                <AuthenticatedLayout>
                  <CreateEvent />
                </AuthenticatedLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/events/:id/edit"
            element={
              <ProtectedRoute>
                <AuthenticatedLayout>
                  <CreateEvent />
                </AuthenticatedLayout>
              </ProtectedRoute>
            }
          />

          {/* Redirect authenticated users from landing page to dashboard */}
          <Route
            path="/landing"
            element={
              isAuthenticated ? <Navigate to="/dashboard" /> : <LandingPage />
            }
          />

          {/* 404 - Catch all unmatched routes */}
          <Route path="*" element={<NotFound />} />
        </Routes>
      </div>
    </Router>
  );
}

export default App;
